import { IEConsentForm } from "@/api/dtos";
import EConsentContext from "@/contexts/EConsentContext";
import useEConsentFormAfterSubmit from "@/hooks/form/useEConsentFormAfterSubmit";
import useEConsentFormAllowSubmit from "@/hooks/form/useEConsentFormAllowSubmit";
import useEConsentFormCancel from "@/hooks/form/useEConsentFormCancel";
import useEConsentFormLabels from "@/hooks/form/useEConsentFormLabels";
import useEConsentFormLookups from "@/hooks/form/useEConsentFormLookups";
import useEConsentFormReadOnly from "@/hooks/form/useEConsentFormReadOnly";
import useEConsentFormSave from "@/hooks/form/useEConsentFormSave";
import useEConsentFormSubmit from "@/hooks/form/useEConsentFormSubmit";
import useEConsentFormSubmitAndReturn from "@/hooks/form/useEConsentFormSubmitAndReturn";
import useEConsentFormValidate from "@/hooks/form/useEConsentFormValidate";
import { Box, Button, Paper, styled, Typography } from "@mui/material";
import { DataForm, EventContext, EventDefinitionContext, FormContext, FormDefinitionContext, FormErrorDisplayMode, FormSubmitButton, IDataFormProps, IEvent, IForm, IFormContext, IInstitution, InstitutionContext, IPatient, LookupsContext, PatientContext, useCrfFormName, useFormMetadata } from "@ngt/opms";
import { ComponentType, ReactElement, useContext } from "react";

type OmitCalculatedTypes<T> = Omit<T, 'validate' | 'save' | 'initialValues' | 'entity'>

export interface ICrfFormPropsAdditionalContent {
    beforeButtons?: ReactElement;
    afterButtons?: ReactElement;
    beforeHeading?: ReactElement;
    afterHeading?: ReactElement;
    beforeContainer?: ReactElement;
    afterContainer?: ReactElement;
    beforeChildren?: ReactElement;
    afterChildren?: ReactElement;
}

/**
 * This interface defines the properties for the Form component.
 */
export interface IEConsentFormProps<TData extends IForm = IForm> extends OmitCalculatedTypes<IDataFormProps<TData>> {
    entity?: string;
    validateDebounce?: number | null;
    hideButtons?: boolean;
    hideSubmit?: boolean;
    hideSubmitAndReturn?: boolean;
    hideCancel?: boolean;
    disablePaper?: boolean;
    disableHeading?: boolean;
    showEventHeading?: boolean;
    requestTypeName?: string;

    submitButtonText?: string;
    submitAndReturnButtonText?: string;
    cancelButtonText?: string;

    afterFormSave?: (institution?: IInstitution | null, patient?: IPatient | null, event?:  IEvent | null, form?: TData | null) => void;
    afterFormSaveAndReturn?: (institution?: IInstitution | null, patient?: IPatient | null, event?: IEvent | null, form?: TData | null) => void;
    onCancel?: () => void;

    AdditionalContent?: ICrfFormPropsAdditionalContent;
}

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'end',

    [theme.breakpoints.only('xs')]: {
        flexDirection: 'column',
        textAlign: 'right',
        gap: theme.spacing(2),

        '& > *': {
            width: '100%'
        }
    }
}))

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */
const EConsentForm = <TData extends IEConsentForm = IEConsentForm>({
    lookups,
    labels,
    hideButtons,
    hideSubmit,
    hideSubmitAndReturn,
    hideCancel,
    disableHeading,
    disablePaper,
    entity,
    validateDebounce,
    readOnly,
    onAfterSubmit,
    afterFormSave,
    afterFormSaveAndReturn,
    onCancel,
    allowSubmit,
    children,
    errorMode,
    AdditionalContent,
    showEventHeading,
    cancelButtonText,
    submitAndReturnButtonText,
    submitButtonText,
    requestTypeName,
    ...formProps
}: IEConsentFormProps<TData>) => {
    const { data: institution } = useContext(InstitutionContext)
    const { data: patient, refresh: patientRefresh } = useContext(PatientContext)
    const { data: event } = useContext(EventContext)
    const { data: eventDefinition } = useContext(EventDefinitionContext)
    const { data: formDefinition } = useContext(FormDefinitionContext)
    const formMetadata = useFormMetadata(formDefinition?.id);
    const { data, mutate } = useContext(FormContext) as unknown as IFormContext<TData>;
    const { data: contextLookups } = useContext(LookupsContext);

    const { participantAccessGuid, userType, readOnly: patientReadOnly } = useContext(EConsentContext)

    const resolvedRequestTypeName = requestTypeName ?? formMetadata?.name;
    const save = useEConsentFormSave<TData>(resolvedRequestTypeName, participantAccessGuid, mutate, userType, patient?.id);
    const validate = useEConsentFormValidate<TData>(resolvedRequestTypeName, participantAccessGuid, validateDebounce ?? 500, userType, patient?.id);
    const formLookups = useEConsentFormLookups(lookups, contextLookups);
    const formLabels = useEConsentFormLabels(labels, formDefinition);

    const formReadOnly = useEConsentFormReadOnly(userType, data, patientReadOnly ?? readOnly);

    const onAfterSubmitToUse = useEConsentFormAfterSubmit<TData>(institution, patient, event, userType, participantAccessGuid, onAfterSubmit, afterFormSave, afterFormSaveAndReturn, patientRefresh)
    const allowSubmitToUse = useEConsentFormAllowSubmit<TData>(allowSubmit);
    
    const onFormSubmit = useEConsentFormSubmit<TData>();

    const onFormSubmitAndReturn = useEConsentFormSubmitAndReturn<TData>();

    const onFormCancel = useEConsentFormCancel(userType, participantAccessGuid, onCancel);

    const formName = useCrfFormName(entity, formDefinition);

    const Container: ComponentType<any> = disablePaper ? Box : Paper;

    return (
        <DataForm
            errorMode={
                errorMode ?? (
                    formReadOnly ? 
                        FormErrorDisplayMode.AlwaysShowAllErrors : 
                        FormErrorDisplayMode.AutoShowAllErrors 
                )
            }
            {...formProps}
            initialValue={data}
            validate={validate}
            save={save}
            entity={formName}
            lookups={formLookups}
            labels={formLabels}
            readOnly={formReadOnly}
            onAfterSubmit={onAfterSubmitToUse}
            allowSubmit={allowSubmitToUse}
        >
            {AdditionalContent?.beforeHeading}
            {
                !disableHeading && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box>
                            <Typography
                                color="secondary"
                                variant="h1"
                                gutterBottom
                            >
                                {formName}
                            </Typography>
                        </Box>
                        {
                            eventDefinition?.name && showEventHeading && (
                                <Box>
                                    <Typography
                                        color="secondary"
                                        variant="h1"
                                        gutterBottom
                                    >
                                        {eventDefinition.name}
                                    </Typography>
                                </Box>
                            )
                        }
                    </Box>
                )
            }
            {AdditionalContent?.afterHeading}
            {AdditionalContent?.beforeContainer}
            <Container>
                {AdditionalContent?.beforeChildren}
                {children}
                {AdditionalContent?.afterChildren}
                {AdditionalContent?.beforeButtons}
                {
                    !hideButtons && !formReadOnly && (
                        <ButtonGroup>
                            {
                                !hideSubmit && (
                                    <FormSubmitButton
                                        variant="contained"
                                        color="primary"
                                        onClick={onFormSubmit}
                                    >
                                        {submitButtonText ?? 'Submit'}
                                    </FormSubmitButton>
                                )
                            }
                            {
                                !hideSubmitAndReturn && (
                                    <FormSubmitButton
                                        variant="contained"
                                        color="primary"
                                        onClick={onFormSubmitAndReturn}
                                    >
                                        
                                        {submitAndReturnButtonText ?? 'Submit And Return'}
                                    </FormSubmitButton>
                                )
                            }
                            {
                                !hideCancel && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={onFormCancel}
                                    >
                                        {cancelButtonText ?? 'Cancel'}
                                    </Button>
                                )
                            }
                        </ButtonGroup>
                    )
                }
                {
                    !hideButtons && formReadOnly && (
                        <ButtonGroup>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onFormCancel}
                            >
                                {cancelButtonText ?? 'Back'}
                            </Button>
                        </ButtonGroup>
                    )
                }
                {AdditionalContent?.afterButtons}
            </Container>
            {AdditionalContent?.afterContainer}
        </DataForm>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default EConsentForm;
